import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Edit, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';

const ValidationDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reviewAction, setReviewAction] = useState('approve');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load pending tickets
      const ticketsRes = await fetch(`${BACKEND_URL}/api/validation/tickets/pending?limit=100`);
      const ticketsData = await ticketsRes.json();
      if (ticketsData.success) {
        setTickets(ticketsData.tickets);
      }

      // Load stats
      const statsRes = await fetch(`${BACKEND_URL}/api/validation/stats`);
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handleReview = async (ticketId, action, editedCorrection = null) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/validation/tickets/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_id: ticketId,
          action: action,
          edited_correction: editedCorrection
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`✅ ${result.message}`);
        setSelectedTicket(null);
        loadData(); // Reload
      } else {
        alert(`❌ Error: ${result.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Review error:', error);
      alert('❌ Failed to submit review');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading validation data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-white text-4xl font-bold mb-2">🔬 Algorithm Validation Dashboard</h1>
          <p className="text-cyan-400 text-lg">Self-Learning Astrology System V2</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Total Predictions</p>
                  <p className="text-white text-3xl font-bold">{stats.total_predictions}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-cyan-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Correct</p>
                  <p className="text-green-400 text-3xl font-bold">{stats.correct_predictions}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Failed</p>
                  <p className="text-red-400 text-3xl font-bold">{stats.failed_predictions}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm">Accuracy</p>
                  <p className="text-cyan-400 text-3xl font-bold">{stats.accuracy_percentage}%</p>
                </div>
                <AlertCircle className="w-8 h-8 text-cyan-400" />
              </div>
            </div>
          </div>
        )}

        {/* Pending Tickets */}
        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-white text-2xl font-bold">📋 Pending Review Tickets ({tickets.length})</h2>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/60 text-lg">✅ No pending tickets - All validations passed!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="bg-white/5 rounded-lg p-6 border border-white/10 hover:border-cyan-400/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white text-xl font-semibold">{ticket.person_name}</h3>
                      <p className="text-cyan-400 text-sm">{ticket.domain.toUpperCase()} - {ticket.algorithm_id}</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
                      Pending Review
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">🔮 Predicted</p>
                      <p className="text-white">
                        {ticket.predicted.date_range.start} - {ticket.predicted.date_range.end}
                      </p>
                      <p className="text-white/40 text-xs">Confidence: {(ticket.predicted.confidence * 100).toFixed(0)}%</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">✅ Actual</p>
                      <p className="text-white">{ticket.actual?.date || 'Not found'}</p>
                      <p className="text-white/40 text-xs">Type: {ticket.actual?.event_type || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
                    <p className="text-red-400 text-sm font-semibold mb-1">Failed at Step {ticket.failed_step.step_number}</p>
                    <p className="text-white/80 text-sm">{ticket.failed_step.description}</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Approve correction for ${ticket.person_name}?`)) {
                          handleReview(ticket.ticket_id, 'approve');
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Reject correction for ${ticket.person_name}?`)) {
                          handleReview(ticket.ticket_id, 'reject');
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTicket(ticket);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setSelectedTicket(null)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/20" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-white text-2xl font-bold">{selectedTicket.person_name}</h2>
                <p className="text-cyan-400">{selectedTicket.domain.toUpperCase()}</p>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-white/60 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* AI Analysis */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <h3 className="text-white font-semibold mb-2">🤖 AI Analysis</h3>
                <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTicket.ai_analysis}</p>
              </div>

              {/* Suggested Corrections */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <h3 className="text-white font-semibold mb-3">💡 Suggested Corrections</h3>
                {selectedTicket.suggested_corrections.map((correction, idx) => (
                  <div key={idx} className="mb-4 pb-4 border-b border-white/10 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-cyan-400 font-semibold">{idx + 1}. {correction.description}</p>
                      <span className="text-white/60 text-sm">{(correction.confidence * 100).toFixed(0)}% confident</span>
                    </div>
                    <p className="text-white/70 text-sm mb-2">{correction.implementation?.rationale}</p>
                    <div className="bg-black/30 rounded p-2">
                      <p className="text-green-400 text-xs font-mono">{correction.implementation?.action}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    handleReview(selectedTicket.ticket_id, 'approve');
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-semibold"
                >
                  <CheckCircle className="w-5 h-5" />
                  Approve Correction
                </button>
                <button
                  onClick={() => {
                    handleReview(selectedTicket.ticket_id, 'reject');
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-semibold"
                >
                  <XCircle className="w-5 h-5" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationDashboard;